<script lang="ts">
    import SearchIcon from '$lib/assets/icons/SearchIcon.svelte';
    import { SearchApi } from '$lib/api/searchApi';
    import { createQuery } from '@tanstack/svelte-query';
    import type { SearchParams, SearchResponse } from 'typesense/lib/Typesense/Documents';
    import type { ProductCollection } from '$lib/api/types/typesenseApiTypes';
    import { t } from 'svelte-i18n';
    import { useLocaleContext, useLocaleISOContext } from '$lib/context';
    import { page } from '$app/stores';
    import { searchFilter } from '$lib/stores';
    import { updateSearch } from '$lib/util/searchUtil';
    import { onMount } from 'svelte';
    import { slide } from 'svelte/transition';
    import { browser } from '$app/environment';

    const lang = useLocaleContext();
    const isoCode = useLocaleISOContext();

    let classes = '';
    let inputClasses =
        '!mt-0 input min-w-64 min-h-12 flex-grow !border-transparent !bg-white placeholder:!text-primary';
    let buttonClasses = 'bg-primary size-12 justify-center flex items-center';
    export let iconWidth = 23;
    export let iconStrokeWidth = 1;
    export let primarySearch: boolean = false;
    export let searchHintOptions: {
        class?: string;
        containerClass?: string;
        enableTooltip?: boolean;
        enableSearchHints?: boolean;
    } = {};

    export { classes as class, inputClasses, buttonClasses };

    export let showButton = true;
    export let query_by: string[] = [];
    export let facet_by: string | undefined = undefined;
    export let limit: number = 10;
    export let additionalParams: SearchParams | undefined = undefined;
    export let enableOnInput: boolean = false;
    export let currentPage: number = 1;
    export let filterPrice: { min: number; max: number } = { min: 5, max: 499 };

    export let searchResult: SearchResponse<ProductCollection> | undefined;

    //arrow navigation
    let highlightedIndex = -1;
    let searchHits = [];

    let value = '';
    if (browser) {
        value = $page.url.searchParams.get('q') ?? '';
    }
    $: if ($page.url.pathname === `/${lang}/search` && browser) updateSearch(lang, { q: value });

    $: queryEnabled = !!(value || !enableOnInput);
    $: searchQuery = createQuery({
        queryKey: [
            'search',
            'products',
            {
                ...additionalParams,
                q: value ?? '*',
                query_by: [
                    'name',
                    'size',
                    'type',
                    'taste',
                    'year',
                    'line',
                    'location',
                    'category',
                    'vdp',
                    ...query_by
                ].join(','),
                infix: 'fallback',
                per_page: limit,
                page: currentPage,
                facet_by,
                max_facet_values: 100,
                filter_by:
                    `active:=true && price:[${filterPrice.min}..${filterPrice.max}]` +
                    (Object.keys($searchFilter).length > 0
                        ? ' && ' +
                          Object.entries($searchFilter)
                              // eslint-disable-next-line @typescript-eslint/no-unused-vars
                              .filter(([key, option]) => option.length > 0)
                              .map(([key, values]) => `${key}:[${values.join(',')}]`)
                              .join(' && ')
                        : '')
            } satisfies SearchParams
        ] as const,
        queryFn: ({ queryKey, signal }) => SearchApi.search(queryKey[1], queryKey[2], signal),
        staleTime: 1000 * 60 * 5,
        enabled: queryEnabled
    });

    $: if ($searchQuery.data) {
        searchResult = $searchQuery.data;
    }

    $: searchHits = searchResult?.hits ?? [];

    //arrow navigation
    function scrollToHighlighted() {
        setTimeout(() => {
            const el = document.querySelector('.search-result-item.highlighted');
            if (el) {
                el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }, 10);
    }

    /* Hotkeys and Events */
    let searchContainer: HTMLDivElement;
    let searchInput: HTMLInputElement;
    export let showHints = false;
    onMount(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && primarySearch) {
                event.preventDefault();
                searchInput?.focus();
            }
            if (!showHints || searchHits.length === 0) return;

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                highlightedIndex = (highlightedIndex + 1) % searchHits.length;
                scrollToHighlighted();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                highlightedIndex = (highlightedIndex - 1 + searchHits.length) % searchHits.length;
                scrollToHighlighted();
            } else if (event.key === 'Enter' && highlightedIndex >= 0) {
                event.preventDefault();
                const hit = searchHits[highlightedIndex];
                if (hit?.document?.productNumber) {
                    window.location.href = `/${lang}/product/${hit.document.productNumber}`;
                }
            } else if (event.key === 'Escape') {
                showHints = false;
            }
        };

        const handleFocus = (event: FocusEvent) => {
            if (event.type === 'focusin') {
                showHints = true;
            } else {
                setTimeout(() => {
                    showHints = false;
                }, 160);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        searchContainer?.addEventListener('focusin', handleFocus);
        searchContainer?.addEventListener('focusout', handleFocus);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            searchContainer?.removeEventListener('focusout', handleFocus);
        };
    });
</script>

<div bind:this={searchContainer} class="{classes} relative">
    <form action="/{lang}/search" method="get" class="flex" autocomplete="off">
        <input
            bind:this={searchInput}
            name="q"
            type="search"
            class={inputClasses}
            placeholder={$t('shop.search.input-placeholder')}
            bind:value
        />

        {#if Object.keys($searchFilter).length > 0}
            <input name="filter" type="hidden" value={JSON.stringify($searchFilter)} />
        {/if}
        {#if showButton}
            <button type="submit" class={buttonClasses}>
                <SearchIcon width={iconWidth} strokeWidth={iconStrokeWidth} />
            </button>
        {/if}
    </form>
    {#if (searchHintOptions.enableSearchHints ?? false) && showHints}
        <div class={searchHintOptions.containerClass || 'absolute bottom-0 left-0 h-0'}>
            <div
                transition:slide={{ duration: 200, axis: 'y' }}
                class={searchHintOptions.class || 'mt-[0.88rem] min-w-[22rem] max-w-[23rem]'}
            >
                <div class="flex flex-col bg-neutral w-full py-4 gap-2">
                    <p class="px-5 text-sm text-neutral-500">{$t('shop.search.products')}</p>
                    <div class="flex flex-col">
                        {#each searchResult?.hits ?? [] as hit, i}
                            <a
                                transition:slide={{ duration: 100, axis: 'y' }}
                                href="/{lang}/product/{hit.document.productNumber}"
                                class="search-result-item px-5 py-1 uppercase w-full text-primary tracking-widest font-light hover:bg-white/10 flex justify-between gap-10 {highlightedIndex ===
                                i
                                    ? 'highlighted bg-white/10'
                                    : ''}"
                            >
                                <span class="text-nowrap overflow-hidden">{hit.document.name[isoCode]}</span>
                                {#if hit.document.year || hit.document.size}
                                    <p
                                        class="hidden sm:flex text-neutral-500 flex-shrink-0 font-normal justify-between w-28 text-sm items-center"
                                    >
                                        <span>{hit.document.size?.translations?.[isoCode] ?? ''}</span>
                                        <span
                                            >{hit.document.year?.translations?.[isoCode]?.length < 5
                                                ? hit.document.year.translations[isoCode]
                                                : ''}</span
                                        >
                                    </p>
                                {/if}
                            </a>
                        {/each}
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>
